'use client';

import { type PropsWithChildren, useCallback, useContext } from 'react';
import { usePathname } from 'next/navigation';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { useQuickSearchModal } from '@frameio/quick-search/contexts';
import { CommandRegistryHelpDialogContext } from '@frameio/shared/commands';
import { useDispatchAppState } from '@frameio/shared/contexts';
import { AppSheetContext } from '@frameio/shared/contexts/AppSheetContext';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useFeedbackDialogContext } from '@frameio/shared/contexts/ModalContext/ModalContext';
import { useCurrentPageType } from '@frameio/shared/routing';
import { getLink } from '@frameio/shared/routing/getLink';
import { getLinkForWorkspaces } from '@frameio/shared/routing/pages/workspaces';
import { Tooltip } from '@frameio/vapor';
import { IconPrimaryNavHomeFill } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/WorkspacesButton';
import { PrimaryNavButton } from './PrimaryNavButton';
import { StyledIcon } from './StyledIcon';
import { usePointerDownLink } from './usePointerDownLink';

TEXT.DEFINE({
  /** Tooltip and accessibility label for navigating to the Home/Workspaces area from the primary navigation */
  workspaces: 'Home',
});

export function WorkspaceButton({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const currentPageType = useCurrentPageType();
  const isWebClient = useIsMatchingClientName('web');

  const link = isWebClient
    ? getLink({
        page: 'workspaces',
      })
    : getLinkForWorkspaces(
        {
          page: 'workspaces',
        },
        true
      );
  const bindNavigation = usePointerDownLink(link);

  const { isFeedbackDialogOpen } = useFeedbackDialogContext();

  const { id: activeSheetId } = useContext(AppSheetContext);

  const { isModalOpen: isQuickSearchModalOpen } = useQuickSearchModal();

  const { isKeyboardShortcutsHelpDialogOpen } = useContext(
    CommandRegistryHelpDialogContext
  );

  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const isWorkspacesPath = pathname === '/' || pathname === '/workspaces';
  const workspacesIconActive =
    currentPageType === 'workspaces' &&
    (isGlobalNavEnabled ? isWorkspacesPath : true) &&
    !activeSheetId &&
    !isQuickSearchModalOpen &&
    !isKeyboardShortcutsHelpDialogOpen &&
    !isFeedbackDialogOpen;

  const dispatch = useDispatchAppState();
  const handleOnClick = useCallback(() => {
    dispatch({ type: 'CLEAR_SCROLL_TO_WORKSPACE' });
  }, [dispatch]);

  if (isGlobalNavEnabled) {
    return (
      <Tooltip
        placement={isWebClient ? 'right' : 'bottom'}
        title={TEXT.workspaces}
      >
        <PrimaryNavButton
          containerProps={bindNavigation}
          current={workspacesIconActive ? 'page' : false}
          icon={<IconPrimaryNavHomeFill aria-hidden={true} size={24} />}
          label={TEXT.workspaces}
          onClick={handleOnClick}
          testid="primary-nav-workspaces-button"
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip
      placement={isWebClient ? 'right' : 'bottom'}
      title={TEXT.workspaces}
    >
      <StyledIcon
        {...bindNavigation}
        aria-current={workspacesIconActive ? 'page' : false}
        aria-label={TEXT.workspaces}
        data-testid="primary-nav-workspaces-button"
        onClick={handleOnClick}
      >
        {children}
      </StyledIcon>
    </Tooltip>
  );
}
